import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { ConfigProvider, App as AntApp, Spin } from "antd";
import frFR from "antd/locale/fr_FR";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// Pages
import LoginPage from "./pages/LoginPage";
import SuperAdminLayout from "./layouts/SuperAdminLayout";
import AdminLayout from "./layouts/AdminLayout";
import SuperAdminDashboard from "./pages/superadmin/Dashboard";
import SuperAdminTenants from "./pages/superadmin/Tenants";
import SuperAdminProfile from "./pages/superadmin/Profile";
import SuperAdminUsers from "./pages/superadmin/Users";
import SuperAdminNotifications from "./pages/superadmin/Notifications";
import SuperAdminSettings from "./pages/superadmin/Settings";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminDrivers from "./pages/admin/Drivers";
import AdminVehicles from "./pages/admin/Vehicles";
import AdminMissions from "./pages/admin/Missions";
import AdminSettings from "./pages/admin/Settings";
import AdminUsers from "./pages/admin/Users";
import AdminCompanySettings from "./pages/admin/CompanySettings";
import AdminMissionTypes from "./pages/admin/MissionTypes";
import AdminProfile from "./pages/admin/Profile";
import AdminFuel from "./pages/admin/Fuel";
import AdminTracking from "./pages/admin/Tracking";
import AdminMissionReports from "./pages/admin/MissionReports";
import AdminPerformanceReports from "./pages/admin/PerformanceReports";
import AdminCostReports from "./pages/admin/CostReports";
import AdminMaintenance from "./pages/admin/Maintenance";
import NotFound from "./pages/NotFound";
import Forbidden from "./pages/Forbidden";

// Components
import ProtectedRoute from "./components/ProtectedRoute";

// Hooks et stores
import { useAuthStore, useAuthInitialization } from "./stores/authStore";

// Styles
import "antd/dist/reset.css";
import "./styles/main.css";

// Configuration React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Configuration du thème Ant Design v5
const theme = {
  token: {
    colorPrimary: "#1890ff",
    borderRadius: 6,
    wireframe: false,
  },
  components: {
    Layout: {
      siderBg: "#001529",
      triggerBg: "#1890ff",
    },
    Button: {
      borderRadius: 6,
    },
    Card: {
      borderRadius: 8,
    },
  },
};

// Wrapper component for SuperAdminLayout with Outlet
const SuperAdminLayoutWrapper: React.FC = () => {
  return (
    <SuperAdminLayout>
      <Outlet />
    </SuperAdminLayout>
  );
};

// Wrapper component for AdminLayout with Outlet
const AdminLayoutWrapper: React.FC = () => {
  return (
    <AdminLayout>
      <Outlet />
    </AdminLayout>
  );
};

// Main App Component
const AppContent: React.FC = () => {
  const { user, isLoading, isAuthenticated, isInitialized, initialize } =
    useAuthStore();

  // Initialize authentication on app load
  useEffect(() => {
    // Only initialize if not already done
    if (!isInitialized) {
      initialize().catch((err) => {
        console.error("Failed to initialize auth:", err);
      });
    }
  }, [isInitialized, initialize]);

  // Show loading spinner during initialization
  if (!isInitialized || isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <Spin size="large" tip="Initialisation de l'application..." />
        </div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            isAuthenticated && user ? (
              <Navigate
                to={user.role === "super_admin" ? "/super-admin" : "/admin"}
                replace
              />
            ) : (
              <LoginPage />
            )
          }
        />

        {/* Super Admin Routes */}
        <Route
          path="/super-admin"
          element={
            <ProtectedRoute requiredRole="super_admin">
              <SuperAdminLayoutWrapper />
            </ProtectedRoute>
          }
        >
          <Route index element={<SuperAdminDashboard />} />
          <Route path="dashboard" element={<SuperAdminDashboard />} />
          <Route path="tenants" element={<SuperAdminTenants />} />
          <Route path="tenants/:id" element={<SuperAdminTenants />} />
          <Route path="settings" element={<SuperAdminSettings />} />
          <Route path="settings/users" element={<SuperAdminUsers />} />
          <Route path="profile" element={<SuperAdminProfile />} />
          <Route path="notifications" element={<SuperAdminNotifications />} />
        </Route>

        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayoutWrapper />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="drivers" element={<AdminDrivers />} />
          <Route path="drivers/new" element={<AdminDrivers />} />
          <Route path="drivers/:id" element={<AdminDrivers />} />
          <Route path="vehicles" element={<AdminVehicles />} />
          <Route path="vehicles/new" element={<AdminVehicles />} />
          <Route path="vehicles/:id" element={<AdminVehicles />} />
          <Route path="missions" element={<AdminMissions />} />
          <Route path="missions/new" element={<AdminMissions />} />
          <Route path="missions/:id" element={<AdminMissions />} />

          {/* Settings Routes */}
          <Route path="settings" element={<AdminSettings />} />
          <Route path="settings/users" element={<AdminUsers />} />
          <Route path="settings/company" element={<AdminCompanySettings />} />
          <Route
            path="settings/mission-types"
            element={<AdminMissionTypes />}
          />

          {/* Profile Route */}
          <Route path="profile" element={<AdminProfile />} />

          {/* Fleet Management Routes */}
          <Route path="fleet/maintenance" element={<AdminMaintenance />} />
          <Route path="fleet/fuel" element={<AdminFuel />} />
          <Route path="fleet/tracking" element={<AdminTracking />} />

          {/* Reports Routes */}
          <Route path="reports/missions" element={<AdminMissionReports />} />
          <Route
            path="reports/performance"
            element={<AdminPerformanceReports />}
          />
          <Route path="reports/costs" element={<AdminCostReports />} />
        </Route>

        {/* Error Pages */}
        <Route path="/403" element={<Forbidden />} />
        <Route path="/404" element={<NotFound />} />

        {/* Root redirect */}
        <Route
          path="/"
          element={
            isAuthenticated && user ? (
              <Navigate
                to={user.role === "super_admin" ? "/super-admin" : "/admin"}
                replace
              />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* Catch all - 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
};

// Root App avec les providers
function App() {
  // Initialize auth on app mount
  useAuthInitialization();

  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider locale={frFR} theme={theme}>
        <AntApp>
          <AppContent />
        </AntApp>
      </ConfigProvider>
    </QueryClientProvider>
  );
}

export default App;
